<template>
  <gg-card>
    <gg-card-header>
      <v-row>
        <v-col md="6">
          Feature Lists
        </v-col>
        <v-col md="6" class="text-right font-sm">
          <gg-btn :disabled="!featureListingAllowed" color="primary" @click="openCreateModal()">
            Add Feature List
          </gg-btn>
        </v-col>
      </v-row>
    </gg-card-header>

    <gg-card-text v-if="!featureListingAllowed">
      <v-row>
        <v-col>
          You don't have enough permissions for this action
        </v-col>
      </v-row>
    </gg-card-text>
    <gg-card-text v-else>
      <v-data-table
        :items="featureList"
        :loading="loading"
        :headers="headers"
      >
        <template v-slot:item.manage="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <gg-btn color="primary" v-bind="attrs" v-on="on">
                Manage <v-icon right>mdi-chevron-down</v-icon>
              </gg-btn>
            </template>
            <v-list>
              <v-list-item @click="openEditModal(item.name)">
                <v-list-item-title>
                  <v-icon small class="pb-1">mdi-pencil</v-icon>
                  <span>
                    Edit
                  </span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="deleteFeatureList(item.name)">
                <v-list-item-title>
                  <v-icon small class="pb-1">mdi-trash-can-outline</v-icon>
                  Delete
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </gg-card-text>

    <gg-modal :is-active="isCreateModal" @close="isCreateModal=false">
      <template v-slot:toolbar>
        Create Feature List
      </template>
      <template v-slot:body>
        <gg-text-field
          outlined
          placeholder="Enter new Feature List name"
          v-model="newFeatureList.featurelist"
          data-vv-name="featurelist"
          data-vv-as="Feature List name"
          v-validate="'required'"
          :error-messages="errors.collect('featurelist')"
          @keyup.enter="createFeatureList"
        ></gg-text-field>
      </template>
      <template v-slot:actions>
        <gg-btn primary @click="createFeatureList" :loading="isCreatingFeatureList">
          Create
        </gg-btn>
      </template>
    </gg-modal>

    <gg-modal :is-active="isEditModal" @close="isEditModal=false">
      <template v-slot:toolbar>
        Add Features to List
      </template>
      <template v-slot:title>
        <v-checkbox
          :label="`Select all features for package ${editedFeatureList}`"
          style="margin: 0; padding: 0"
          @change="selectAll"
        />
      </template>
      <template v-slot:body>
        <gg-text-field
          outlined
          placeholder="Type for searching"
          v-model="filter"
          @input="filterFormData"
        ></gg-text-field>
        <div style="display: grid; grid-template-columns: 1fr 1fr;">
          <v-checkbox
            v-for="(feature, index) in formData"
            :key="index"
            :id="feature.name"
            :label="feature.label"
            v-model="formData[index].value"
            style="margin: 0; padding: 0"
            :disabled="isLoadingFeaturesForList"
          >
            <template v-slot:label>
              <label :for="feature.name">{{ feature.label }}</label>
              <v-chip
                v-if="feature.isPlugin"
                class="ma-2"
                label
                color="primary"
                text-color="white"
                small
              >
                Plugin
              </v-chip>
            </template>
          </v-checkbox>
        </div>
      </template>
      <template v-slot:actions>
        <gg-btn primary @click="applyFeatures" :loading="isUpdatingFeatureList">
          Save Changes
        </gg-btn>
      </template>
    </gg-modal>
  </gg-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  RESELLER_SERVICE_PRIVILEGE_PACKAGE_ADD,
} from '@/consts/service'

export default {
  name: 'ResellerFeatureLists',

  data () {
    return {
      headers: [
        {
          text: 'Feature List Name',
          value: 'name',
        },
        {
          text: 'Manage',
          value: 'manage',
          align: 'center',
          sortable: false,
        }
      ],
      formData: [],
      checkboxesList: [],
      filter: '',
      editedFeatureList: '',
      newFeatureList: { featurelist: '' },
      isCreateModal: false,
      isEditModal: false,
      isLoadingFeaturesForList: false,
      isCreatingFeatureList: false,
      isUpdatingFeatureList: false,
      loading: false,
      loadingFeatures: false,
    }
  },
  computed: {
    ...mapGetters([ 'getResellerFeatureLists', 'getResellerFeatures', 'hosting_detail', 'getResellerPrivileges' ]),
    hostingid () {
      return this.hosting.id
    },
    featureList () {
      return this.getResellerFeatureLists.map((featureList) => {
        return {
          name: featureList,
        }
      })
    },
    featureListPrefix () {
      return this.hosting_detail.user + '_'
    },
    featureListingAllowed () {
      return !!this.getResellerPrivileges[RESELLER_SERVICE_PRIVILEGE_PACKAGE_ADD]
    },
  },

  methods: {
    ...mapActions([
      'fetchResellerPackages',
      'fetchResellerFeatureLists',
      'fetchResellerThemes',
      'createResellerPackage',
      'deleteResellerPackage',
      'updateResellerPackage',
      'fetchResellerFeatures',
      'fetchFeatureListData',
      'createResellerFeatureList',
      'updateResellerFeatureList',
      'deleteResellerFeatureList',
    ]),
    async boot () {
      if (!this.featureListingAllowed) {
        return
      }
      this.loading = true
      await Promise.all([
        this.fetchResellerFeatureLists(),
        this.loadFeatures(),
      ])
      this.loading = false
    },

    async loadFeatures () {
      this.loadingFeatures = true
      await this.fetchResellerFeatures()
      this.loadingFeatures = false
      this.setCheckboxesList()
      this.filterFormData('')
    },

    setCheckboxesList () {
      const checkboxes = this.getResellerFeatures.map((feature) => {
        return {
          label: feature.name,
          name: feature.id,
          value: false,
          isAddon: feature.is_cpaddon === '1',
          isPlugin: feature.is_plugin === '1',
        }
      })
      checkboxes.sort((a, b) => {
        return a.label.localeCompare(b.label)
      })
      this.checkboxesList.push(...checkboxes)
    },
    filterFormData (searchValue) {
      const filteredFeatures = this.checkboxesList.filter((feature) => {
        return feature.label.toLowerCase().includes(searchValue)
      })
      this.formData.length = 0
      this.formData.push(...filteredFeatures)
    },
    selectAll (selecting) {
      this.checkboxesList.forEach((feature) => {
        feature.value = selecting
      })
    },
    openCreateModal () {
      this.newFeatureList.featurelist = ''
      this.isCreateModal = true
    },
    openEditModal (featureListName) {
      this.isCreateModal = false
      this.editedFeatureList = featureListName
      this.loadFeatureListData(this.editedFeatureList)
      this.isEditModal = true
    },
    async createFeatureList () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        return
      }
      const featureListName = `${this.featureListPrefix}${this.newFeatureList.featurelist}`
      this.isCreatingFeatureList = true
      const isSuccess = await this.createResellerFeatureList({ featurelist: featureListName })
      this.isCreatingFeatureList = false
      if (isSuccess === false) {
        return
      }
      this.fetchResellerFeatureLists()
      this.isCreateModal = false
      this.newFeatureList.featurelist = ''
      this.openEditModal(featureListName)
    },
    async deleteFeatureList (featureListName) {
      await this.deleteResellerFeatureList({ featurelist: featureListName })
      this.fetchResellerFeatureLists()
    },
    async loadFeatureListData (featureListName) {
      this.isLoadingFeaturesForList = true
      const { features } = await this.fetchFeatureListData({ featurelist: featureListName })
      this.isLoadingFeaturesForList = false
      this.setCheckboxListValues(features)
    },
    setCheckboxListValues (featuresData) {
      featuresData.forEach((featureData) => {
        const index = this.checkboxesList.findIndex((feature) => {
          return feature.name === featureData.id
        })
        if (index < 0) {
          return
        }
        this.checkboxesList[index].value = featureData.value === '1'
      })
    },
    async applyFeatures () {
      this.isUpdatingFeatureList = true
      const data = {
        featurelist: this.editedFeatureList,
      }
      this.checkboxesList.forEach((checkbox) => {
        data[checkbox.name] = Number(checkbox.value)
      })
      const isSuccess = await this.updateResellerFeatureList(data)
      this.isUpdatingFeatureList = false
      if (isSuccess === false) {
        return
      }
      this.isEditModal = false
    }

  },
  watch: {
    getResellerPrivileges () {
      this.boot()
    }
  },
  created () {
    this.boot()
  }
}
</script>
